import { useEffect, useState } from 'react'
import { useAdminAuth } from '../contexts/AdminAuthContext'
import { useAdminService, AdminTenant } from '../services/adminService'
import { Feedback, FeedbackStatus } from '../services/feedbackService'
import { Button, Card, CardHeader, CardTitle, CardContent, Alert, Modal } from '../components'
import { feedbackStatusClasses, FEEDBACK_TYPE_LABELS, FEEDBACK_STATUS_LABELS } from '../statusStyles'

const FEEDBACK_STATUSES: FeedbackStatus[] = ['new', 'in_progress', 'planned', 'resolved', 'wont_fix']

const TenantsPanel: React.FC = () => {
  const { fetchTenants } = useAdminService()
  const [tenants, setTenants] = useState<AdminTenant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    load()
  }, [])

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setTenants(await fetchTenants())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Tenants ({tenants.length})</CardTitle>
          <Button type="button" size="sm" variant="secondary" onClick={load}>
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert type="error" className="mb-4">
            {error}
          </Alert>
        )}

        {loading ? (
          <p className="text-gray-600">Loading...</p>
        ) : tenants.length === 0 ? (
          <p className="text-gray-600">No tenants found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Company</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Plan</th>
                  <th className="py-2 pr-4">Merchant configured</th>
                  <th className="py-2 pr-4">Created</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.email} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium text-gray-900">{t.companyName || '—'}</td>
                    <td className="py-2 pr-4">{t.email}</td>
                    <td className="py-2 pr-4">{t.planKey || '—'}</td>
                    <td className="py-2 pr-4">{t.merchantId && t.merchantType ? 'Yes' : 'No'}</td>
                    <td className="py-2 pr-4">{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const FeedbackPanel: React.FC = () => {
  const { fetchFeedback, updateFeedback, getFeedbackAttachmentDownloadUrl } = useAdminService()
  const [items, setItems] = useState<Feedback[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | 'all'>('all')

  const [selected, setSelected] = useState<Feedback | null>(null)
  const [notesDraft, setNotesDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    load()
  }, [])

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setItems(await fetchFeedback())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback')
    } finally {
      setLoading(false)
    }
  }

  const openDetail = (item: Feedback) => {
    setSelected(item)
    setNotesDraft(item.adminNotes)
    setSaveError(null)
  }

  const applyLocalUpdate = (updated: Feedback) => {
    setItems((prev) => prev.map((f) => (f.id === updated.id ? updated : f)))
    setSelected(updated)
  }

  const handleStatusChange = async (status: FeedbackStatus) => {
    if (!selected) return
    setSaving(true)
    setSaveError(null)
    try {
      applyLocalUpdate(await updateFeedback(selected.id, { status }))
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update status')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveNotes = async () => {
    if (!selected) return
    setSaving(true)
    setSaveError(null)
    try {
      applyLocalUpdate(await updateFeedback(selected.id, { adminNotes: notesDraft }))
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save notes')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenAttachment = async (s3Key: string) => {
    if (!selected) return
    try {
      const url = await getFeedbackAttachmentDownloadUrl(selected.id, s3Key)
      window.open(url, '_blank')
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to open attachment')
    }
  }

  const filtered = statusFilter === 'all' ? items : items.filter((f) => f.status === statusFilter)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle>Feedback ({filtered.length})</CardTitle>
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as FeedbackStatus | 'all')}
              className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
            >
              <option value="all">All statuses</option>
              {FEEDBACK_STATUSES.map((s) => (
                <option key={s} value={s}>{FEEDBACK_STATUS_LABELS[s]}</option>
              ))}
            </select>
            <Button type="button" size="sm" variant="secondary" onClick={load}>
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert type="error" className="mb-4">
            {error}
          </Alert>
        )}

        {loading ? (
          <p className="text-gray-600">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-600">No feedback found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Title</th>
                  <th className="py-2 pr-4">Tenant</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => (
                  <tr
                    key={f.id}
                    className="border-b last:border-0 cursor-pointer hover:bg-gray-50"
                    onClick={() => openDetail(f)}
                  >
                    <td className="py-2 pr-4">{FEEDBACK_TYPE_LABELS[f.type]}</td>
                    <td className="py-2 pr-4 font-medium text-gray-900">{f.title}</td>
                    <td className="py-2 pr-4">{f.tenantEmail}</td>
                    <td className="py-2 pr-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${feedbackStatusClasses(f.status)}`}>
                        {FEEDBACK_STATUS_LABELS[f.status]}
                      </span>
                    </td>
                    <td className="py-2 pr-4">{new Date(f.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.title || 'Feedback'} size="lg">
        {selected && (
          <div className="space-y-4">
            {saveError && <Alert type="error">{saveError}</Alert>}

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>{FEEDBACK_TYPE_LABELS[selected.type]}</span>
              <span>&middot;</span>
              <span>{selected.tenantEmail}</span>
              <span>&middot;</span>
              <span>{new Date(selected.createdAt).toLocaleString()}</span>
            </div>

            {selected.description && (
              <p className="text-sm text-gray-800 whitespace-pre-wrap border border-gray-200 rounded-lg p-3 bg-gray-50">
                {selected.description}
              </p>
            )}

            {selected.pageUrl && (
              <p className="text-xs text-gray-500 break-all">Page: {selected.pageUrl}</p>
            )}

            {selected.attachments.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Attachments</p>
                <ul className="space-y-1">
                  {selected.attachments.map((a) => (
                    <li key={a.s3Key}>
                      <button
                        type="button"
                        onClick={() => handleOpenAttachment(a.s3Key)}
                        className="text-sm text-primary hover:underline"
                      >
                        {a.fileName}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={selected.status}
                onChange={(e) => handleStatusChange(e.target.value as FeedbackStatus)}
                disabled={saving}
                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5"
              >
                {FEEDBACK_STATUSES.map((s) => (
                  <option key={s} value={s}>{FEEDBACK_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                rows={4}
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
              />
              <Button type="button" size="sm" className="mt-2" onClick={handleSaveNotes} disabled={saving}>
                {saving ? 'Saving…' : 'Save notes'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  )
}

export const AdminDashboard: React.FC = () => {
  const { logout } = useAdminAuth()
  const [tab, setTab] = useState<'tenants' | 'feedback'>('tenants')

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/conveyo/logo.svg" alt="Conveyo Logo" className="w-8 h-8" />
            <h1 className="logo">
              <span className="logo-conveyo">conveyo</span>
              <span className="logo-dot">.</span>
              <span className="logo-one">one</span>
            </h1>
            <span className="text-sm font-semibold text-gray-500 ml-2">Admin</span>
          </div>
          <Button variant="ghost" onClick={logout}>
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-1 border-b border-gray-200 mb-6">
          {([
            ['tenants', 'Tenants'],
            ['feedback', 'Feedback'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === key ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'tenants' ? <TenantsPanel /> : <FeedbackPanel />}
      </main>
    </div>
  )
}
